import { MODULES, type ModuleDefinition, type ModuleKey } from "../modules/registry";
import { canAccessModule, canAccessModuleAtHotel } from "./access";
import { PROPERTY_OPTIONS } from "./accessGrants";
import { resolveEffectiveAccess, type EffectiveAccessContext } from "./effectiveAccess";
import type { AuthenticatedUser } from "./types";

export type ResolvedPropertyContext = {
  id: string;
  label: string;
  isDefault: boolean;
};

export type ResolvedNavigationContext = {
  access: EffectiveAccessContext;
  properties: ResolvedPropertyContext[];
  accessibleModules: ModuleDefinition[];
  visibleModules: ModuleDefinition[];
  defaultLandingPath: string | null;
  state: "ready" | "no_role" | "no_modules";
};

function getModulePath(module: ModuleDefinition): string {
  if (module.path) return module.path;
  return `/module/${module.key}`;
}

function getPropertyLabel(tenantId: string) {
  const knownProperty = PROPERTY_OPTIONS.find((property) => property.id === tenantId);
  if (knownProperty) return knownProperty.label;
  return tenantId.toUpperCase() === "HQ" ? "Xentros HQ" : `Xentros ${tenantId}`;
}

const HOTEL_SCOPED_MODULES = new Set<ModuleKey>([
  "pms",
  "pos",
  "ems",
  "voip",
  "wms",
  "revenue",
  "guests",
  "cctv",
  "keycoder",
  "garage",
  "parking-preview",
]);

function canAccessNavigationModule(
  user: AuthenticatedUser | null | undefined,
  moduleKey: ModuleKey,
  hotelId?: string | null,
) {
  if (HOTEL_SCOPED_MODULES.has(moduleKey)) {
    if (!hotelId && Array.isArray(user?.propertyPermissions)) return false;
    return canAccessModuleAtHotel(user, moduleKey, hotelId);
  }
  return canAccessModule(user, moduleKey);
}

function resolveAccessibleModules(user: AuthenticatedUser | null | undefined, hotelId?: string | null): ModuleDefinition[] {
  return MODULES
    .filter((module) => canAccessNavigationModule(user, module.key as ModuleKey, hotelId))
    .sort((left, right) => left.order - right.order);
}

export function resolveVisibleModules(user: AuthenticatedUser | null | undefined, hotelId?: string | null): ModuleDefinition[] {
  return resolveAccessibleModules(user, hotelId).filter((module) => module.showOnDesktop !== false);
}

export function resolveNavigationContext(user: AuthenticatedUser | null | undefined, hotelId?: string | null): ResolvedNavigationContext {
  const access = resolveEffectiveAccess(user);
  const properties = access.memberships.map((membership) => ({
    id: membership.tenantId,
    label: getPropertyLabel(membership.tenantId),
    isDefault: Boolean(membership.isDefault) || membership.tenantId === access.tenantId,
  }));
  const accessibleModules = resolveAccessibleModules(user, hotelId);
  const visibleModules = resolveVisibleModules(user, hotelId);
  const defaultLandingPath = accessibleModules[0] ? getModulePath(accessibleModules[0]) : null;

  if (!access.hasAssignedRole) {
    return {
      access,
      properties,
      accessibleModules,
      visibleModules,
      defaultLandingPath,
      state: "no_role",
    };
  }

  if (accessibleModules.length === 0) {
    return {
      access,
      properties,
      accessibleModules,
      visibleModules,
      defaultLandingPath,
      state: "no_modules",
    };
  }

  return {
    access,
    properties,
    accessibleModules,
    visibleModules,
    defaultLandingPath,
    state: "ready",
  };
}
